import { FuelIcon, FuelTag, FuelTypography } from '@frete.com/fuel-react';
import { FuelFlex } from '@frete.com/fuel-station-react';
import { DS_TOKENS } from 'constants/styled.contants';
import { useCompanyData } from 'hooks/useCompanyData';
import React, { FC, useState } from 'react';
import { IRiskManagementLastConsultationsResponse } from 'services/riskManagement/types';
import { brlCurrencyFormatter } from 'utils';
import CompaniesListModal from '../CompaniesListModal';
import { defaultRiskManagementValues } from '../constants';
import LastConsultations from '../LastConsultations';
import ConsultationLabels from '../LastConsultations/ConsultationLabels';
import { PartnerOptions } from '../SelectPartner/types';
import * as Styled from './styled';

interface CardProps {
  isApprovalValid: boolean;
  totalApproves: number;
  hasLastSearches: boolean;
  lastConsultationsData: IRiskManagementLastConsultationsResponse;
  isFetched: boolean;
  uniquePartnerNames: string[];
}

const Card: FC<CardProps> = ({
  isApprovalValid,
  totalApproves,
  hasLastSearches,
  lastConsultationsData,
  isFetched,
  uniquePartnerNames,
}) => {
  const { useGetComboFeature } = useCompanyData();

  const registerRiskManagementComboFeature = useGetComboFeature('register-gr');

  const getRiskManagementRegistrationPrice = () =>
    registerRiskManagementComboFeature?.price || defaultRiskManagementValues.register;

  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  return (
    <FuelFlex
      defaultProps={{
        flexDirection: 'column',
        backgroundColor: DS_TOKENS.color.background.neutral.weak,
        padding: 'km4',
        gap: 'km4',
        borderRadius: 'small',
      }}
      data-testid="card-component"
    >
      <FuelFlex defaultProps={{ justifyContent: 'flex-start', gap: 'km3' }}>
        <FuelIcon size="small" name="one-check" iconStyle="filled" data-testid="one-check-icon" />
        <FuelTypography weight="medium" size="body-md" color="neutral-strong" data-testid="approve-rate-text">
          {isApprovalValid ? (
            <>
              Com <Styled.BlueStrong>94,3%</Styled.BlueStrong> chance de nova aprovação
            </>
          ) : (
            <>
              Com <Styled.BlueStrong>90,2%</Styled.BlueStrong> chance de aprovação
            </>
          )}
        </FuelTypography>
      </FuelFlex>
      {hasLastSearches && <LastConsultations totalApproves={totalApproves} isApprovalValid={isApprovalValid} />}
      {!isApprovalValid && (
        <>
          <FuelFlex defaultProps={{ justifyContent: 'flex-start', gap: 'km3' }}>
            <FuelIcon size="small" name="user-area" iconStyle="filled" data-testid="user-area-not-approved-icon" />
            <FuelTypography weight="medium" size="body-md" color="neutral-strong" data-testid="not-approved-text">
              Motorista ainda não foi aprovado
            </FuelTypography>
          </FuelFlex>
          <FuelFlex defaultProps={{ justifyContent: 'flex-start', gap: 'km3', alignItems: 'center' }}>
            <FuelIcon size="small" name="risk-management" iconStyle="filled" data-testid="risk-management-icon" />
            <FuelTypography weight="medium" size="body-md" color="neutral-strong" data-testid="gr-partners-text">
              Cadastre o Motorista em nossas{' '}
              <Styled.UnderlinedText onClick={handleOpenModal}>GR parceiras</Styled.UnderlinedText>
            </FuelTypography>
          </FuelFlex>
          <FuelFlex defaultProps={{ justifyContent: 'flex-start', gap: 'km3', alignItems: 'center' }}>
            <FuelIcon size="small" name="clock" iconStyle="filled" data-testid="clock-icon" />
            <FuelTypography weight="medium" size="body-md" color="neutral-strong" data-testid="register-time-text">
              Cadastro em média de <Styled.BlueStrong>10 minutos</Styled.BlueStrong>
            </FuelTypography>
          </FuelFlex>
          <FuelFlex defaultProps={{ justifyContent: 'flex-start', gap: 'km3', alignItems: 'center' }}>
            <FuelIcon size="small" name="money" iconStyle="filled" data-testid="money-icon" />
            <FuelTypography weight="medium" size="body-md" color="neutral-strong" data-testid="promotion-value-text">
              Oferta especial de <Styled.ValueText>R$ 29,90</Styled.ValueText>{' '}
              <Styled.StrongText>por {brlCurrencyFormatter(getRiskManagementRegistrationPrice())}</Styled.StrongText>
            </FuelTypography>
            <FuelTag
              highlight
              label="Promoção"
              shape="rectangle"
              color="green"
              uppercase
              data-testid="promotion-register-tag"
            />
          </FuelFlex>
        </>
      )}
      {isApprovalValid && isFetched && lastConsultationsData && uniquePartnerNames && (
        <ConsultationLabels uniquePartnerNames={uniquePartnerNames as PartnerOptions[]} />
      )}
      <CompaniesListModal handleCloseModal={handleCloseModal} isModalOpen={isModalOpen} />
    </FuelFlex>
  );
};

export default Card;
